import { Modal } from 'antd';
import { useAppSelector, useAppTranslation, useErrTranslation } from 'app/hooks';
import CloseIcon from 'assets/icons/CloseIcon';
import Typo from 'consts/typography';
import React, { useEffect, useState } from 'react';
import { platformStates } from 'redux/platformSlice';
import { useMessageSuccess } from 'hooks/useMessageSuccess';
import depositApi from 'api/depositApi';
import { useHandleResponseError } from 'hooks/useHandleResponseError';
import useUserDetail from 'hooks/useUserDetail';
import useThemes from 'hooks/useThemes';
import { isValidAmount } from './WithdrawModal';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import FormField from 'components/Form/FormField';
import SuccessIcon from 'assets/icons/SuccessIcon';
import { UserBankbookResponseModel } from 'models/wallet';
import ChooseUserBankbookModal from './ChooseUserBankbookModal';

interface AchDebitDepositModalProps {
  title?: string;
  visible: boolean;
  closeModal: () => void;
  className?: string;
}

type FormInputs = {
  amount: string;
  routingNumber: string;
  accountNumber: string;
};

const AchDebitDepositModal: React.FunctionComponent<AchDebitDepositModalProps> = (props) => {
  const { title = '', visible, closeModal, className = '' } = props;
  const { currentTheme } = useThemes();
  const { isPhone, isTablet } = useAppSelector(platformStates);
  const handleResponseError = useHandleResponseError();
  const t = useAppTranslation();
  const et = useErrTranslation();
  const { showToast, contextHolder } = useMessageSuccess();
  const { isUserLoggedIn } = useUserDetail();
  const [isShowSuccess, setIsShowSuccess] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOpenSavedUserBankbookModal, setIsOpenSavedUserBankbookModal] = useState<boolean>(false);

  const validationSchema = yup.object({
    amount: yup.string().required(et('error.validate.field.amount.required')),
    routingNumber: yup
      .string()
      .required(et('error.validate.field.routingNumber.required'))
      .length(9, et('error.validate.field.routingNumber.invalid'))
      .matches(/^[0-9]+$/, et('error.validate.field.routingNumber.invalid')),
    accountNumber: yup
      .string()
      .required(et('error.validate.field.accountNumber.required'))
      .min(5, et('error.validate.field.accountNumber.invalid'))
      .max(17, et('error.validate.field.accountNumber.invalid'))
      .matches(/^[0-9]+$/, et('error.validate.field.accountNumber.invalid')),
  });

  const {
    handleSubmit,
    setValue,
    control,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm<FormInputs>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });

  const onSubmit = async (data: FormInputs) => {
    setIsLoading(true);
    const { ok, error } = await depositApi.createFiatDeposit({
      amount: Number(data.amount),
      routingNumber: data.routingNumber,
      accountNumber: data.accountNumber,
    });
    setIsLoading(false);

    if (ok) {
      setIsShowSuccess(true);
    }

    if (error) handleResponseError(error);
  };

  const onCloseModal = () => {
    reset({
      amount: '',
      accountNumber: '',
      routingNumber: '',
    });
    setIsShowSuccess(false);
    closeModal();
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const regex = /^(?!.*\..*\..*)(?=.*\d)(?!.*\.\d{3,})(?!.{11,}$)[\d.]{0,10}$/;
    const value = event.target.value;

    if ((regex.test(value) && isValidAmount(value)) || value === '') {
      setValue('amount', event.target.value, {
        shouldValidate: true,
      });
    }
  };

  const onChangeInputNumber = (
    input: 'routingNumber' | 'accountNumber',
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const regex = /^[\d]+$/;
    const value = event.target.value;

    if (regex.test(value) || value === '') {
      setValue(input, event.target.value, {
        shouldValidate: true,
      });
    }
  };

  const handleChooseUserBankbookClick = () => {
    setIsOpenSavedUserBankbookModal((prev) => !prev);
  };

  const onChooseSavedBankbook = (item: UserBankbookResponseModel) => {
    setIsOpenSavedUserBankbookModal(false);
    if (item.accountNumber && item.routingNumber) {
      setValue('accountNumber', item.accountNumber, {
        shouldValidate: true,
      });
      setValue('routingNumber', item.routingNumber, {
        shouldValidate: true,
      });
    }
  };

  useEffect(() => {
    reset({
      amount: '',
      accountNumber: '',
      routingNumber: '',
    });
  }, [isShowSuccess]);

  return (
    <Modal
      className={className}
      title={title}
      visible={visible}
      onCancel={onCloseModal}
      footer={null}
      centered
      maskClosable={false}
      closeIcon={<CloseIcon isDarkMode={currentTheme === 'dark'} width="20" height="20" />}
      width={600}
    >
      {!isShowSuccess ? (
        <form className="center-content-col gap-2">
          <div className="form-input">
            <div className="flex justify-between items-center">
              <label
                className={`${
                  isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
                } font-medium primary-text-color`}
                htmlFor="amount"
              >
                {t('cashier.amount')}
              </label>
              <span
                className={`${
                  isPhone ? Typo.p8 : isTablet ? Typo.p6 : Typo.p4
                } font-medium primary-text-color underline cursor-pointer`}
                onClick={handleChooseUserBankbookClick}
              >
                {t('cashier.savedBankbook')}
              </span>
            </div>
            <FormField
              control={control}
              errors={errors}
              placeholder={t('placeholder.enterAmount')}
              name="amount"
              component="input"
              type="text"
              onChange={onChange}
              isAboveError={false}
            />
          </div>
          <div className="form-input">
            <label
              className={`${
                isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
              } font-medium primary-text-color`}
              htmlFor="routingNumber"
            >
              {t('cashier.routingNumber')}
            </label>
            <FormField
              control={control}
              errors={errors}
              placeholder={t('placeholder.enterRoutingNumber')}
              name="routingNumber"
              component="input"
              type="text"
              isAboveError={false}
              maxLength={9}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onChangeInputNumber('routingNumber', e)
              }
            />
          </div>
          <div className="form-input">
            <label
              className={`${
                isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
              } font-medium primary-text-color`}
              htmlFor="accountNumber"
            >
              {t('cashier.accountNumber')}
            </label>
            <FormField
              control={control}
              errors={errors}
              placeholder={t('placeholder.enterAccountNumber')}
              name="accountNumber"
              component="input"
              type="text"
              isAboveError={false}
              maxLength={17}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onChangeInputNumber('accountNumber', e)
              }
            />
          </div>
          {isUserLoggedIn && (
            <div className="withdraw-balance">
              <div className="withdraw-balance__left"></div>
              <div className="withdraw-balance__right">
                <button
                  disabled={isLoading || isDirty || !isValid}
                  className={`mt-2 button-generate ${
                    isLoading || isDirty || !isValid ? 'disabled' : ''
                  }`}
                  type="submit"
                  onClick={handleSubmit(onSubmit)}
                >
                  {t('deposit.title')}
                </button>
              </div>
            </div>
          )}
        </form>
      ) : (
        <div className="center-content-col gap-3 withdraw--success text-center">
          <SuccessIcon width="40" height="40" />
          <p className="title">{t('deposit.success.title')}</p>
          <p className="message">{`${t('deposit.success.fiatMessage')}`}</p>
          <button type="button" className="continue" onClick={onCloseModal}>
            {t('deposit.close')}
          </button>
        </div>
      )}
      {contextHolder}
      {isOpenSavedUserBankbookModal && (
        <ChooseUserBankbookModal
          title={t('accountSubMenu.bankbook')}
          visible={isOpenSavedUserBankbookModal}
          closeModal={handleChooseUserBankbookClick}
          className="common-modal withdraw-modal"
          onChoose={onChooseSavedBankbook}
          filteredType={['ACH']}
        />
      )}
    </Modal>
  );
};

export default AchDebitDepositModal;
